import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ImageCroppedEvent,
  ImageCropperComponent,
  ImageTransform,
} from 'ngx-image-cropper';
import { ToastrService } from 'ngx-toastr';
import { FileContentModel } from 'services/models/fileContent';
import { ToasterService } from '../../../../services/toaster.service';

export enum MediaEditorAcceptFileType {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PDF = 'application/pdf',
}

export type MediaEditorResult = {
  file: File;
  fileContent: FileContentModel;
};

@Component({
  selector: 'app-media-editor-modal',
  templateUrl: './media-editor-modal.component.html',
  styleUrls: ['./media-editor-modal.component.scss'],
})
export class MediaEditorModalComponent implements OnInit, AfterViewInit {
  @ViewChild(ImageCropperComponent) imageCropper!: ImageCropperComponent;
  @ViewChild('fileUpload') fileUpload!: ElementRef<HTMLInputElement>;

  @Input() title = 'Selecionar mídia';
  @Input() aspectRatio = 4 / 3;
  @Input() maintainAspectRatio = false;
  @Input() containWithinAspectRatio = false;
  @Input() resizeToWidth: number | undefined;
  @Input() resizeToHeight: number | undefined;
  @Input() preselectedFile: File | null = null;
  @Input() autoCrop = true;
  @Input() uploadButtonText = 'Escolher arquivo';
  @Input() uploadContentText = 'Clique para um selecionar arquivo...';
  @Input() saveButtonText = 'Confirmar';
  @Input() acceptFileTypes = 'image/png, image/jpeg, image/jpg';
  @Input() successMessage = '';
  @Input() showFlipButtons = false;
  @Input() allowMoveImage = true;
  @Input() showUploadButton = false;
  @Input() description = '';
  @Input() maxFileSize = 21 * 1024 * 1024; // 21 MB padrão
  @Input() acceptedExtensions: string[] = ['.jpeg', '.jpg', '.png'];

  imageFormat: 'png' | 'jpg' | 'jpeg' = 'png';
  imageChangedEvent: any = null;
  croppedImage: SafeUrl | null = null;

  isPdf = false;
  filePreviewUrl: SafeResourceUrl | null = null;

  fileModel: FileContentModel | null = null;
  file: File | null = null;

  transform: ImageTransform = {
    scale: 1,
    rotate: 0,
    flipH: false,
    flipV: false,
  };

  constructor(
    public activeModal: NgbActiveModal,
    private toasterService: ToasterService,
    private sanitizer: DomSanitizer,
    private _toastr: ToastrService
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadPreselectedFileIfAny());
  }

  private loadPreselectedFileIfAny(): void {
    const file = this.preselectedFile;
    if (!file || !this.fileUpload?.nativeElement) {
      return;
    }

    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.acceptedExtensions.includes(extension)) {
      return;
    }

    if (file.size > this.maxFileSize) {
      return;
    }

    if (file.type === 'application/pdf') {
      return;
    }

    const input = this.fileUpload.nativeElement;
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    input.files = dataTransfer.files;
    this.handleImage({ target: input });
  }

  onFileChanged(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;

    // Bloquear PDF no cropper de imagens
    if (file.type === 'application/pdf' && !this.acceptFileTypes.includes('application/pdf')) {
      this._toastr.warning(
        'Arquivos PDF não são permitidos para edição de imagens. Use apenas imagens (PNG, JPEG, JPG).',
        'Tipo de arquivo inválido',
        {
          positionClass: 'toast-bottom-center',
          timeOut: 3000,
        }
      );

      this.resetInput();
      return;
    }

    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!this.acceptedExtensions.includes(extension)) {
      this._toastr.warning(
        `Tipo de arquivo não permitido. Aceitos: ${this.acceptedExtensions.join(', ')}`,
        'Arquivo inválido',
        {
          positionClass: 'toast-bottom-center',
          timeOut: 3000,
        }
      );

      this.resetInput();
      return;
    }

    if (file.size > this.maxFileSize) {
      const maxMb = Math.floor(this.maxFileSize / 1024 / 1024);

      this._toastr.warning(
        `O arquivo "${file.name}" ultrapassa o limite de ${maxMb} MB.`,
        'Arquivo muito grande',
        {
          positionClass: 'toast-bottom-center',
          timeOut: 3000,
        }
      );

      this.resetInput();
      return;
    }

    if (file.type === 'application/pdf') {
      this.handlePdf(file);
    } else {
      this.handleImage(event);
    }

    this.resetInput();
  }

  private handleImage(event: any): void {
    this.resetState();
    this.isPdf = false;
    this.imageChangedEvent = event;

    const file = event.target.files[0];
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      const extension = file.name.split('.').pop()!;

      this.fileModel = {
        fileName: file.name,
        extension,
        encodedBase64: this.extractBase64(base64),
      };
    };
  }

  imageCropped(event: ImageCroppedEvent): void {
    if (!this.fileModel || !event.base64) return;

    this.croppedImage = this.sanitizer.bypassSecurityTrustUrl(event.base64);
    this.fileModel.encodedBase64 = this.extractBase64(event.base64);
  }

  private handlePdf(file: File): void {
    this.resetState();
    this.isPdf = true;
    this.file = file;

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const base64 = reader.result as string;

      this.filePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64);

      this.fileModel = {
        fileName: file.name,
        extension: 'pdf',
        encodedBase64: this.extractBase64(base64),
      };
    };
  }

  confirm(): void {
    if (!this.fileModel) return;

    const file = this.isPdf ? this.file! : this.fileModelToFile(this.fileModel);

    if (this.successMessage) {
      this.toasterService.showSuccess(this.successMessage);
    }

    this.activeModal.close({ file: file, fileContent: this.fileModel } as MediaEditorResult);
  }

  remove(): void {
    this.resetState();
  }

  selectNewFile(): void {
    if (this.fileUpload?.nativeElement) {
      this.fileUpload.nativeElement.click();
    }
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  rotateLeft() {
    this.transform = { ...this.transform, rotate: this.transform.rotate - 90 };
  }

  rotateRight() {
    this.transform = { ...this.transform, rotate: this.transform.rotate + 90 };
  }

  zoomIn() {
    this.transform = { ...this.transform, scale: this.transform.scale + 0.1 };
  }

  zoomOut() {
    if (this.transform.scale > 0.2) {
      this.transform = { ...this.transform, scale: this.transform.scale - 0.1 };
    }
  }

  flipHorizontal() {
    this.transform = { ...this.transform, flipH: !this.transform.flipH };
  }

  flipVertical() {
    this.transform = { ...this.transform, flipV: !this.transform.flipV };
  }

  private resetState(): void {
    this.imageChangedEvent = null;
    this.croppedImage = null;
    this.filePreviewUrl = null;
    this.fileModel = null;
    this.file = null;
    this.isPdf = false;
    this.transform = { scale: 1, rotate: 0, flipH: false, flipV: false };
  }

  private extractBase64(data: string): string {
    return data.split('base64,')[1];
  }

  private fileModelToFile(model: FileContentModel): File {
    const byteString = atob(model.encodedBase64);
    const arrayBuffer = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      arrayBuffer[i] = byteString.charCodeAt(i);
    }

    return new File([arrayBuffer], model.fileName, {
      type: this.getMimeType(model.extension),
    });
  }

  private resetInput(): void {
    setTimeout(() => {
      if (this.fileUpload?.nativeElement) {
        this.fileUpload.nativeElement.value = '';
      }
    });
  }

  public cropperReady(): void { }

  private getMimeType(ext: string): string {
    switch (ext.toLowerCase()) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
      case 'jfif':
        return 'image/jpeg';
      case 'webp':
        return 'image/webp';
      case 'pdf':
        return 'application/pdf';
      default:
        return 'application/octet-stream';
    }
  }

  public get descriptionuploadContentText(): string {
    const exts = this.acceptedExtensions.map(e => e.replace('.', '').toUpperCase()).join(', ');
    const maxMb = Math.floor(this.maxFileSize / 1024 / 1024);

    return `Somente arquivos do tipo ${exts}, com tamanho máximo de ${maxMb} MB, são permitidos.`;
  }
}
